import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Grid,
  Button,
  useTheme,
  useMediaQuery,
  CardActionArea, // Imported for card click functionality
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import QuantityEditor from "./QuantityEditor";
import ProductDetails from "../ProductDetails"; // Imported for product details modal
import { fetchMoreDetails } from "../api/fetchMoreDetails";
import isMobile from "../isMobile";
import { isProductExpired } from "../api/productUtils";

const ProductCard = ({
  item,
  index,
  editIndex,
  newQuantity,
  handleEditQuantity,
  handleSaveQuantity,
  handleIncrement,
  handleDecrement,
  deleteItem,
  data,
  setData,
}) => {
  const [message, setMessage] = useState("");
  const [openDetails, setOpenDetails] = useState(false); // State for modal open/close
  const [selectedProduct, setSelectedProduct] = useState(null); // State for selected product
  const mobileView = isMobile();
  const charLimit = mobileView ? 30 : 60; // Character limit for truncation
  const productExpired = isProductExpired(item);

  const handleCardClick = async () => {
    if (!productExpired) {
      let details;
      const { global_id } = item;
      if (global_id) {
        details = await fetchMoreDetails(global_id);
      } else {
        details = item;
      }
      setSelectedProduct(details);
      setOpenDetails(true);
    }
  };

  const fetchUrl = async (globalId) => {
    const details = await fetchMoreDetails(globalId);
    if (details) {
      return details.url;
    }
  };

  const handleRemove = (e) => {
    setMessage("Product Removed");
    deleteItem(e, item);
    setTimeout(() => setMessage(""), 2000);
  };

  // Truncate the item name if necessary
  const truncatedName =
    item.name.length > charLimit
      ? item.name.substring(0, charLimit) + "..."
      : item.name;

  const productPrice = (item.current_price * item.quantity).toFixed(2);

  return (
    <>
      <Grid
        item
        xs={6}
        sm={4}
        md={3}
        key={`${item.id}-${item.name}`}
        style={{ display: "flex" }}
      >
        <CardActionArea onClick={handleCardClick}>
          <Card
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              padding: "10px",
              border: "1px solid #d3d3d3",
              borderRadius: "8px",
              boxShadow: "none",
              minHeight: "350px",
              transition: "height 0.3s ease",
              filter: productExpired ? "grayscale(100%) opacity(50%)" : "none",
            }}
          >
            <Box sx={{ position: "relative" }}>
              {/* {productExpired && 'Deal Expired'} */}
              <CardMedia
                component="img"
                height="140"
                image={item.clean_image_url || item.image_url || ""}
                alt={item.name}
                style={{ objectFit: "contain", marginBottom: "10px" }}
              />
              <Box sx={{ position: "absolute", bottom: 10, left: 0 }}>
                <QuantityEditor
                  quantity={item.quantity}
                  editIndex={editIndex}
                  index={index}
                  newQuantity={newQuantity}
                  handleIncrement={handleIncrement}
                  handleDecrement={handleDecrement}
                  handleSaveQuantity={handleSaveQuantity}
                  handleEditQuantity={handleEditQuantity}
                />
              </Box>
            </Box>
            <CardContent
              style={{
                padding: "0",
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="body1"
                  component="div"
                  style={{
                    fontSize: "0.9rem",
                    color: "#000",
                    textAlign: "left",
                    flexShrink: 0,
                  }}
                >
                  {truncatedName}
                  {item.name.length > charLimit && (
                    <span
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card click
                        handleCardClick();
                      }}
                      style={{ color: "blue", cursor: "pointer" }}
                    >
                      {" "}
                      Read more
                    </span>
                  )}
                </Typography>
                {/* Spacer to push content to the bottom */}
                <Box sx={{ flexGrow: 1 }} />
                <Typography
                  variant="body2"
                  color="text.primary"
                  style={{
                    fontSize: "0.9rem",
                    color: "#000",
                    marginBottom: "5px",
                    textAlign: "left",
                    fontWeight: "bold",
                    flexShrink: 0,
                  }}
                >
                  ${productPrice}{" "}
                  {item.quantity > 1 && `(${item.current_price} Per Item)`}
                </Typography>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "5px",
                    flexShrink: 0,
                  }}
                >
                  {item.merchant_logo && (
                    <img
                      src={item.merchant_logo}
                      alt={`${item.merchant_name} Logo`}
                      style={{
                        maxWidth: "20px",
                        maxHeight: "20px",
                        objectFit: "contain",
                        marginRight: "5px",
                      }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    style={{
                      fontSize: "0.8rem",
                      textAlign: "left",
                      marginBottom: "5px",
                    }}
                  >
                    {item.merchant_name || item.merchant}
                  </Typography>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    flexShrink: 0,
                  }}
                >
                  {message && (
                    <>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        style={{ fontSize: "0.8rem", marginRight: "5px" }}
                      >
                        {message}
                      </Typography>
                      <CheckIcon color="success" />
                    </>
                  )}
                  {!message && !productExpired && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      size={mobileView ? "small" : "large"}
                      style={{
                        fontSize: "0.7rem",
                        textTransform: "none",
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card click
                        handleRemove(e);
                      }}
                    >
                      Remove
                    </Button>
                  )}
                  {item.global_id && !productExpired && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size={mobileView ? "small" : "large"}
                      style={{
                        fontSize: "0.7rem",
                        textTransform: "none",
                      }}
                      onClick={async (e) => {
                        e.stopPropagation(); // Prevent card click
                        e.preventDefault();
                        const url = await fetchUrl(item.global_id);
                        window.location.href = url;
                      }}
                    >
                      Buy Now
                    </Button>
                  )}
                </div>
              </Box>
            </CardContent>
          </Card>
          {productExpired && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "8px",
                flexDirection: "column",
                zIndex: 1,
                textAlign: "center",
                padding: "10px",
              }}
            >
              <Typography variant="h6" component="div">
                {message ? (
                  <>
                    <Typography
                      variant='h6'
                      style={{
                        fontSize: '1rem',
                        color: "white",
                        fontWeight: 'bold'
                      }}
                    >
                      {message}
                    </Typography>
                  </>
                ) : (
                  "Deal Expired"
                )}
              </Typography>
              <Button
                variant="contained"
                color="error"
                size={mobileView ? "small" : "large"}
                style={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                }}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent card click
                  handleRemove(e);
                }}
              >
                Remove
              </Button>
            </div>
          )}
        </CardActionArea>
      </Grid>
      {/* Render the ProductDetails modal */}
      {selectedProduct && (
        <ProductDetails
          open={openDetails}
          handleClose={() => setOpenDetails(false)}
          productDetails={selectedProduct}
          products={data}
          setProducts={setData}
        />
      )}
    </>
  );
};

export default ProductCard;
